import * as E from "fp-ts/Either"
import * as O from "fp-ts/Option"
import { pipe } from "fp-ts/function"

// Jour de l'organisme : seul calcul d'une date `AAAA-MM-JJ`.
// Fuseau servi par le serveur, avec l'utilisateur.
// Jamais le jour UTC ni celui du navigateur.

/** Fuseau de repli, le même que le serveur. */
export const DEFAULT_TENANT_TIME_ZONE = "Europe/Paris"

const ISO_DAY_LENGTH = 10
const ISO_MONTH_LENGTH = 7
const DAY_MS = 24 * 60 * 60 * 1000

const dayFormats = new Map<string, Intl.DateTimeFormat>()

function newDayFormat(timeZone: string): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" })
}

/** Format du jour dans `timeZone`. Fuseau inconnu : Paris, comme le serveur. */
function dayFormat(timeZone: string): Intl.DateTimeFormat {
  return pipe(
    O.fromNullable(dayFormats.get(timeZone)),
    O.getOrElse(() => {
      const created = pipe(
        E.tryCatch(() => newDayFormat(timeZone), (error) => error),
        E.getOrElse(() => newDayFormat(DEFAULT_TENANT_TIME_ZONE)),
      )
      dayFormats.set(timeZone, created)
      return created
    }),
  )
}

/**
 * Jour `AAAA-MM-JJ` de l'instant `now` dans le fuseau de l'organisme.
 *
 * @example
 * organisationDay(new Date("2026-03-14T21:00:00Z"), "Indian/Reunion") // "2026-03-15"
 */
export function organisationDay(now: Date, timeZone: string): string {
  return dayFormat(timeZone).format(now)
}

/**
 * Partie `AAAA-MM-JJ` d'une date ou d'un horodatage ISO.
 *
 * @example
 * isoDay("2026-10-14T08:00:00Z") // "2026-10-14"
 */
export function isoDay(value: string): string {
  return value.slice(0, ISO_DAY_LENGTH)
}

/**
 * Mois `AAAA-MM` d'une date ou d'un horodatage ISO.
 *
 * @example
 * isoMonth("2026-10-14") // "2026-10"
 */
export function isoMonth(value: string): string {
  return value.slice(0, ISO_MONTH_LENGTH)
}

/**
 * Jour `AAAA-MM-JJ` d'une date construite à minuit UTC.
 *
 * @example
 * utcIsoDay(new Date(Date.UTC(2026, 1, 28))) // "2026-02-28"
 */
export function utcIsoDay(date: Date): string {
  return isoDay(date.toISOString())
}

/**
 * `day` décalé de `days` jours de calendrier.
 *
 * @example
 * shiftIsoDay("2026-02-28", 1) // "2026-03-01"
 */
export function shiftIsoDay(day: string, days: number): string {
  const date = new Date(`${isoDay(day)}T00:00:00Z`)
  date.setUTCDate(date.getUTCDate() + days)
  return utcIsoDay(date)
}

/** Minuit UTC du jour `day`, en millisecondes. */
function dayStartMs(day: string): number {
  return Date.parse(`${isoDay(day)}T00:00:00Z`)
}

/**
 * Jours de calendrier entre `from` et `to`.
 *
 * @example
 * isoDaysBetween("2026-02-28", "2026-03-01") // 1
 */
export function isoDaysBetween(from: string, to: string): number {
  return Math.round((dayStartMs(to) - dayStartMs(from)) / DAY_MS)
}

/** Jour numérique « 09/12/2026 », celui de `toLocaleDateString("fr-FR")`. */
const NUMERIC_DAY: Intl.DateTimeFormatOptions = { day: "2-digit", month: "2-digit", year: "numeric" }
/** Date et heure « 09/12/2026 14:30:00 », celles de `toLocaleString("fr-FR")`. */
const NUMERIC_DATE_TIME: Intl.DateTimeFormatOptions = { ...NUMERIC_DAY, hour: "2-digit", minute: "2-digit", second: "2-digit" }
/** Date et heure à la minute : « 09/12/2026 14:30 ». */
export const DAY_AND_MINUTE: Intl.DateTimeFormatOptions = { ...NUMERIC_DAY, hour: "2-digit", minute: "2-digit" }
/** Heure « 14:30:00 », celle de `toLocaleTimeString("fr-FR")`. */
const NUMERIC_TIME: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit", second: "2-digit" }

const CALENDAR_DAY = /^\d{4}-\d{2}-\d{2}$/

/** Format français dans `timeZone`. Fuseau inconnu : Paris, comme le serveur. */
function frenchFormat(timeZone: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  return pipe(
    E.tryCatch(() => new Intl.DateTimeFormat("fr-FR", { ...options, timeZone }), (error) => error),
    E.getOrElse(() => new Intl.DateTimeFormat("fr-FR", { ...options, timeZone: DEFAULT_TENANT_TIME_ZONE })),
  )
}

/**
 * Date calendaire `AAAA-MM-JJ`, ou jour d'un horodatage, sans conversion.
 * Formatée en UTC, à son propre minuit.
 * Elle ne recule jamais d'un jour à l'ouest d'UTC.
 *
 * @example
 * formatIsoDateFr("2026-07-22") // "22/07/2026"
 * formatIsoDateFr("2026-03-03", { day: "numeric", month: "long", year: "numeric" }) // "3 mars 2026"
 * formatIsoDateFr("à confirmer") // "à confirmer"
 */
export function formatIsoDateFr(isoDate: string, options: Intl.DateTimeFormatOptions = NUMERIC_DAY): string {
  const midnight = new Date(`${isoDay(isoDate)}T00:00:00Z`)
  return Number.isNaN(midnight.getTime()) ? isoDate : frenchFormat("UTC", options).format(midnight)
}

/**
 * Date calendaire formatée comme `formatIsoDateFr`, ou « — » si elle manque.
 *
 * @example
 * formatOptionalIsoDateFr(O.some("2026-07-22")) // "22/07/2026"
 * formatOptionalIsoDateFr(O.none) // "—"
 */
export function formatOptionalIsoDateFr(isoDate: O.Option<string>): string {
  return pipe(
    isoDate,
    O.match(
      () => "—",
      (day) => formatIsoDateFr(day),
    ),
  )
}

/** Met en forme un instant ISO au fuseau de l'organisme. */
type InstantFormatter = (value: string, timeZone: string, options?: Intl.DateTimeFormatOptions) => string

/**
 * Formateur d'instants, avec ses options par défaut.
 * Une date sans heure n'est pas un instant.
 * Elle est rendue comme par `formatIsoDateFr`.
 * Un instant illisible est rendu tel quel.
 */
function instantFormatter(defaults: Intl.DateTimeFormatOptions): InstantFormatter {
  return (value, timeZone, options = defaults) => {
    if (CALENDAR_DAY.test(value)) return formatIsoDateFr(value, options)
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? value : frenchFormat(timeZone, options).format(parsed)
  }
}

/**
 * Jour d'un instant ISO au fuseau de l'organisme : « 09/12/2026 ».
 *
 * @example formatInstantDay("2026-07-15T21:30:00Z", "Indian/Reunion") // "16/07/2026"
 */
export const formatInstantDay: InstantFormatter = instantFormatter(NUMERIC_DAY)

/**
 * Date et heure d'un instant ISO au fuseau de l'organisme.
 *
 * @example formatInstantDateTime("2026-07-15T21:30:00Z", "Indian/Reunion") // "16/07/2026 01:30:00"
 */
export const formatInstantDateTime: InstantFormatter = instantFormatter(NUMERIC_DATE_TIME)

/**
 * Heure d'un instant ISO au fuseau de l'organisme.
 *
 * @example formatInstantTime("2026-07-15T21:30:00Z", "Indian/Reunion", { hour: "2-digit", minute: "2-digit" }) // "01:30"
 */
export const formatInstantTime: InstantFormatter = instantFormatter(NUMERIC_TIME)

const SECOND_MS = 1000

const offsetFormats = new Map<string, Intl.DateTimeFormat>()

/** Décalage du fuseau à l'instant `ms`, en millisecondes. */
function zoneOffsetMs(ms: number, timeZone: string): number {
  const format = pipe(
    O.fromNullable(offsetFormats.get(timeZone)),
    O.getOrElse(() => {
      const created = frenchFormat(timeZone, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hourCycle: "h23",
      })
      offsetFormats.set(timeZone, created)
      return created
    }),
  )
  const parts = format.formatToParts(new Date(ms))
  const part = (type: Intl.DateTimeFormatPartTypes): number =>
    pipe(
      O.fromNullable(parts.find((candidate) => candidate.type === type)),
      O.map((found) => Number(found.value)),
      O.getOrElse(() => 0),
    )
  const month = part("month") - 1
  const date = [part("year"), month, part("day")] as const
  const time = [part("hour"), part("minute"), part("second")] as const
  const wallClock = Date.UTC(...date, ...time)
  const wholeSeconds = Math.floor(ms / SECOND_MS) * SECOND_MS
  return wallClock - wholeSeconds
}

/** Instant d'une heure murale, au fuseau de l'organisme. */
function wallClockInstant(wallClockMs: number, timeZone: string): string {
  const guess = wallClockMs - zoneOffsetMs(wallClockMs, timeZone)
  return new Date(wallClockMs - zoneOffsetMs(guess, timeZone)).toISOString()
}

/**
 * Minuit du jour `day`, au fuseau de l'organisme.
 *
 * @example organisationDayStart("2026-07-16", "Indian/Reunion") // "2026-07-15T20:00:00.000Z"
 */
export function organisationDayStart(day: string, timeZone: string): string {
  return wallClockInstant(dayStartMs(day), timeZone)
}

/** Valeur d'un champ `datetime-local`, secondes permises. */
const DATE_TIME_INPUT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?$/
const DATE_TIME_INPUT_LENGTH = 16

/**
 * Valeur d'un champ `datetime-local` pour un instant.
 * Heure de l'organisme, à la minute.
 * Vide si l'instant manque ou est illisible.
 *
 * @example organisationDateTimeInput("2026-07-15T21:30:00Z", "Indian/Reunion") // "2026-07-16T01:30"
 */
export function organisationDateTimeInput(instant: string | null | undefined, timeZone: string): string {
  return pipe(
    O.fromNullable(instant),
    O.map((value) => Date.parse(value)),
    O.filter((ms) => !Number.isNaN(ms)),
    O.map((ms) => new Date(ms + zoneOffsetMs(ms, timeZone))),
    O.map((wallClock) => wallClock.toISOString().slice(0, DATE_TIME_INPUT_LENGTH)),
    O.getOrElse(() => ""),
  )
}

/**
 * Instant saisi dans un champ `datetime-local`.
 * Heure lue au fuseau de l'organisme.
 * Jamais à celui du navigateur.
 * Aucun instant si la saisie est vide ou incomplète.
 *
 * @example organisationInstantFromInput("2026-07-16T10:00", "Indian/Reunion") // O.some("2026-07-16T06:00:00.000Z")
 */
export function organisationInstantFromInput(value: string, timeZone: string): O.Option<string> {
  return pipe(
    O.some(value.trim()),
    O.filter((input) => DATE_TIME_INPUT.test(input)),
    O.map((input) => (input.length === DATE_TIME_INPUT_LENGTH ? `${input}:00Z` : `${input}Z`)),
    O.map((utcInput) => Date.parse(utcInput)),
    O.filter((ms) => !Number.isNaN(ms)),
    O.map((ms) => wallClockInstant(ms, timeZone)),
  )
}

/**
 * Dernière seconde du jour `day` (23:59:59).
 * Au fuseau de l'organisme.
 *
 * @example organisationDayEnd("2026-07-16", "Indian/Reunion") // "2026-07-16T19:59:59.000Z"
 */
export function organisationDayEnd(day: string, timeZone: string): string {
  const nextDayStart = Date.parse(organisationDayStart(shiftIsoDay(day, 1), timeZone))
  return new Date(nextDayStart - SECOND_MS).toISOString()
}
